import { fetchAuthSession } from 'aws-amplify/auth';
import React, { useEffect, useMemo, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { driverApi } from '../Driver/driverApi';
import { getAllRoles } from '../Onboarding/onboardingApi';
import {
  deleteUser,
  fetchAvailableSupervisors,
  getAllUsers,
  getProfilePhoto,
  transferUsers
} from './employeeApiService';

import ArrowIcon from '../../assets/icons/arrow.svg';
import EmployeeGroupIcon from '../../assets/icons/clarity_employee-group-solid.svg';
import EmployeeIcon from '../../assets/icons/clarity_employee-solid.svg';
import InfoIconSvg from '../../assets/icons/info_icon.svg';
import ArrowUpIcon from '../../assets/icons/majesticons_arrow-up.svg';
import CloseIcon from '../../assets/icons/material-symbols_close-rounded.svg';

import { SearchBar } from '../ui';

function normalizeUsers(users) {
  return users.map(u => {
    // Preserve original user_id for display, but keep original for API calls
    const originalUserId = u.user_id || u.employee_id || u.id;
    return {
      ...u,
      // Keep original user_id if it exists, otherwise use employee_id or id
      user_id: originalUserId || 'N/A',
      // Preserve original user_id separately for image fetching
      original_user_id: u.user_id,
      role_name:
        (typeof u.role_name === 'string' && u.role_name) ||
        (typeof u.spec_role === 'string' && u.spec_role) ||
        'Employee',
      display_id: originalUserId || 'N/A',
    };
  });
}

/**
 * Convert S3 URL to presigned URL
 */
async function convertS3ToPresignedUrl(s3Path) {
  try {
    if (!s3Path || !s3Path.startsWith('s3://')) {
      return s3Path; // Return as-is if not an S3 URL
    }

    const { tokens } = await fetchAuthSession();
    if (!tokens || !tokens.idToken) {
      throw new Error('No authentication token available');
    }

    const apiUrl = 'https://api.innoworx.ai/Stage';
    const response = await fetch(
      `${apiUrl}/s3-presigned?url=${encodeURIComponent(s3Path)}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${tokens.idToken.toString()}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get presigned URL: ${response.status}`);
    }

    const data = await response.json();
    return data.presigned_url || s3Path; // Fallback to original if no presigned URL
  } catch (error) {
    console.error('Error converting S3 URL to presigned URL:', error);
    return null; // Return null on error so placeholder can be used
  }
}

/**
 * Get profile photo with S3 URL handling
 */
async function getProfilePhotoWithS3Handling(userId) {
  try {
    const photo = await getProfilePhoto(userId);
    
    // If photo is an S3 URL, convert it to presigned URL
    if (photo && typeof photo === 'string' && photo.startsWith('s3://')) {
      const presignedUrl = await convertS3ToPresignedUrl(photo);
      return presignedUrl;
    }
    
    return photo;
  } catch (error) {
    console.warn(`Failed to fetch photo for user ${userId}:`, error.message || error);
    return null;
  }
}

function InfoIcon({ onClick }) {
  return (
    <button className="relative z-10" onClick={onClick} aria-label="Info">
      <img src={InfoIconSvg} alt="info" className="w-7 h-7" />
    </button>
  );
}

function CustomDropdown({ options, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="w-full md:w-80 bg-fuchsia-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-300 inline-flex flex-col justify-start items-start relative z-10">
      <div
        className="self-stretch px-3 py-3.5 inline-flex justify-between items-center cursor-pointer"
        onClick={() => setOpen((o) => !o)}
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
          {selected || "Designation"}
        </div>
        <img
  src={ArrowIcon}
  alt="dropdown-arrow"
  className={`w-5 h-5 transform transition-transform duration-300 ${
    open ? 'rotate-180' : 'rotate-0'
  }`}
/>

        
      </div>
      {open && (
        <div className="self-stretch p-3 bg-stone-200 rounded-lg inline-flex flex-col justify-start items-start gap-4 absolute z-20 top-full left-0 w-full">
          {options.map((opt) => (
            <div
              key={opt.value}
              className="self-stretch justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed cursor-pointer hover:bg-stone-300 rounded"
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
              role="option"
            >
              {opt.display}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoBox({ onClose }) {
  return (
    <div
      className="w-64 md:w-72 h-auto md:h-24 absolute left-[-200px] md:left-[-263px] top-[37px] bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.1px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px] overflow-hidden z-50 p-3 md:p-0"
    >
      <div className="w-full md:w-60 absolute md:left-3 md:top-3 text-black text-sm md:text-base font-normal font-['Open_Sans'] leading-tight">
        To Delete an employee, Please transfer the assigned employees to other employee
      </div>
      <button
        onClick={onClose}
        className="w-6 h-6 absolute left-[263px] top-[5px] bg-stone-950/10 rounded-full flex items-center justify-center"
        aria-label="Close info"
      >
        <img src={CloseIcon} alt="close-info" className="w-4 h-4" />
      </button>
    </div>
  );
}

function SubmitWidget({ onClose }) {
  return (
    <div className="fixed inset-0 flex justify-center items-center z-[70] bg-black/30 p-4">
      <div
        className="w-full max-w-[464px] h-auto min-h-[384px] md:h-96 relative bg-neutral-50/50 rounded-xl
        shadow-[0px_15px_30px_rgba(0,0,0,0.10)]
        shadow-[0px_5px_20px_rgba(255,255,255,0.10)]
        shadow-[inset_0px_4px_25px_rgba(255,255,255,0.35)]
        outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px]
        flex flex-col justify-center items-center gap-6 md:gap-8 text-center overflow-hidden p-6 md:p-8"
      >
        <div className="text-black text-xl md:text-2xl font-bold font-figtree leading-7">
          Employee Deleted Successfully
        </div>

        <div className="w-28 h-28 md:w-36 md:h-36 relative flex items-center justify-center">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-green-100 rounded-full flex items-center justify-center">
            <svg 
              className="w-12 h-12 md:w-16 md:h-16 text-green-700" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="3" 
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full max-w-[320px] md:w-80 h-12 md:h-14 px-6 py-3 bg-green-700 rounded-lg flex justify-center items-center"
        >
          <span className="text-white text-lg md:text-xl font-bold font-figtree leading-normal">Done</span>
        </button>
      </div>
    </div>
  );
}

export default function DeleteEmployeeTransfer() {
  // Log screen view
  useScreenLogging('DeleteEmployeeProfile', {
    component: 'DeleteEmployeeProfile',
  });
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [showTransferOptions, setShowTransferOptions] = useState(false);
  const [availableSupervisors, setAvailableSupervisors] = useState([]);
  const [supervisorSearch, setSupervisorSearch] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [transferConfirmed, setTransferConfirmed] = useState(false);
  const [noSupervisors, setNoSupervisors] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const ONLY_DELETE_ROLES = ['guard', 'driver', 'blue_collar_employee', 'employee'];
  const isOnlyDeleteDesignation = ONLY_DELETE_ROLES.includes((selectedRole ?? '').toLowerCase());
  const [isDeleteOnly, setIsDeleteOnly] = useState(false);


  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const fetchedRoles = await getAllRoles();
        const cleaned = fetchedRoles.filter((r) => {
          const name = (r.role_name ?? '').toLowerCase();
          const display = (r.display_name ?? '').toLowerCase();
          return !['admin', 'ceo'].includes(name) && !['admin', 'ceo'].includes(display);
        });
        setRoles(cleaned.map((r) => ({
          display: r.display_name,
          value: r.spec_role ?? r.role_name,
        })));
      } catch (err) {
        console.error('Failed to load roles:', err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedRole) {
      setEmployees([]);
      setSelectedEmployee(null);
      return;
    }
    (async () => {
      try {
        setIsLoading(true);
        let usersRaw = [];
        let driversRaw = [];
        
        // Fetch all users and drivers
        try {
          usersRaw = await getAllUsers();
        } catch (err) {
          console.error('Failed to fetch all users:', err);
        }
        
        // Also fetch drivers separately if role is driver
        if (selectedRole.toLowerCase() === 'driver') {
          try {
            driversRaw = await driverApi.fetchAllDrivers();
          } catch (err) {
            console.error('Failed to fetch drivers:', err);
          }
        }
        
        // Combine users and drivers, removing duplicates
        const allUsersRaw = [...usersRaw, ...driversRaw];
        const uniqueUsers = [];
        const seenIds = new Set();
        
        allUsersRaw.forEach(user => {
          const id = user.user_id || user.employee_id || user.id;
          if (id && !seenIds.has(String(id))) {
            seenIds.add(String(id));
            uniqueUsers.push(user);
          }
        });
        
        // Filter by selected role (case-insensitive matching)
        const normalizedRole = selectedRole.toLowerCase().trim();
        const filteredUsers = uniqueUsers.filter(user => {
          const userSpecRole = (user.spec_role || '').toLowerCase().trim();
          const userRoleName = (user.role_name || '').toLowerCase().trim();
          
          // Match if spec_role or role_name matches the selected role
          return userSpecRole === normalizedRole || userRoleName === normalizedRole;
        });
        
        // Store original user_id mapping before normalization
        const userIdMap = new Map();
        filteredUsers.forEach(orig => {
          const key = orig.employee_id || orig.user_id || orig.id;
          if (key) {
            userIdMap.set(String(key), orig);
          }
        });
        
        const users = normalizeUsers(filteredUsers);
        const withPhotos = await Promise.all(users.map(async (u) => {
          // Try to get user_id for photo fetching - use the original data from map
          const lookupKey = String(u.employee_id || u.user_id || u.id || '');
          const originalUser = userIdMap.get(lookupKey);
          
          // Priority: original user_id > normalized user_id > employee_id > id
          let photoUserId = null;
          if (originalUser) {
            // Use original user_id first, then fallback to employee_id or id
            photoUserId = originalUser.user_id || originalUser.employee_id || originalUser.id;
          } else {
            // Fallback to preserved original_user_id or normalized values
            photoUserId = u.original_user_id || 
                         (u.user_id && u.user_id !== 'N/A' ? u.user_id : null) ||
                         (u.employee_id || u.id || null);
          }
          
          if (!photoUserId || String(photoUserId).trim() === '' || photoUserId === 'N/A') {
            return { ...u, profile_image: null };
          }
          
          try {
            const photo = await getProfilePhotoWithS3Handling(String(photoUserId).trim());
            // Only set photo if we got a valid URL
            if (photo && 
                typeof photo === 'string' && 
                photo.trim() !== '' && 
                !photo.includes('undefined') && 
                photo !== 'null' && 
                photo !== 'undefined' &&
                (photo.startsWith('http://') || photo.startsWith('https://'))) {
              console.log(`✅ Successfully fetched photo for user ${photoUserId}:`, photo.substring(0, 50) + '...');
              return { ...u, profile_image: photo };
            } else {
              console.warn(`⚠️ Invalid photo URL for user ${photoUserId}:`, photo);
              return { ...u, profile_image: null };
            }
          } catch (error) {
            console.warn(`❌ Failed to fetch photo for user ${photoUserId}:`, error.message || error);
            return { ...u, profile_image: null };
          }
        }));
        setEmployees(withPhotos);
        setSelectedEmployee(null);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedRole]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) setFilteredEmployees(employees);
    else
      setFilteredEmployees(employees.filter(e =>
        e.user_name?.toLowerCase().includes(q) ||
        e.display_id?.toLowerCase().includes(q) ||
        e.employee_id?.toString().toLowerCase().includes(q) ||
        e.user_id?.toString().toLowerCase().includes(q)
      ));
  }, [search, employees]);

  async function openTransferOptions() {
    if (!selectedEmployee) return;
    try {
      setIsLoading(true);
      const supervisorsRaw = await fetchAvailableSupervisors();
      const filteredSupers = supervisorsRaw.filter(s =>
        s.user_id !== selectedEmployee.user_id &&
        ((s.spec_role ?? s.role_name ?? '').toLowerCase() === (selectedEmployee.spec_role ?? selectedEmployee.role_name ?? '').toLowerCase())
      );
      const supervisorsWithPhotos = await Promise.all(filteredSupers.map(async s => {
        // Try to get user_id for photo fetching - use original user_id, employee_id, or id
        const photoUserId = s.user_id && s.user_id !== 'N/A' ? s.user_id : 
                           (s.employee_id || s.id || null);
        
        if (!photoUserId || String(photoUserId).trim() === '') {
          return { ...s, profile_image: null };
        }
        
        try {
          const photo = await getProfilePhotoWithS3Handling(photoUserId);
          // Only set photo if we got a valid URL
          if (photo && 
              typeof photo === 'string' && 
              photo.trim() !== '' && 
              !photo.includes('undefined') &&
              (photo.startsWith('http://') || photo.startsWith('https://'))) {
            return { ...s, profile_image: photo };
          }
          return { ...s, profile_image: null };
        } catch (error) {
          console.warn(`Failed to fetch photo for supervisor ${photoUserId}:`, error);
          return { ...s, profile_image: null };
        }
      }));
      setAvailableSupervisors(supervisorsWithPhotos);
      setShowTransferOptions(true);
      setSelectedSupervisor(null);
      setSupervisorSearch('');
      setTransferConfirmed(false);
      setNoSupervisors(false);
      // On mobile, keep employee selected but transfer overlay will show on top
    } catch (err) {
      console.error('Error loading supervisors:', err);
    } finally {
      setIsLoading(false);
    }
  }

  const filteredSupervisors = useMemo(() => {
    const q = supervisorSearch.trim().toLowerCase();
    if (!q) return availableSupervisors;
    return availableSupervisors.filter(s =>
      (s.user_name ?? '').toLowerCase().includes(q) ||
      (s.user_id ?? '').toLowerCase().includes(q)
    );
  }, [availableSupervisors, supervisorSearch]);

  function confirmTransfer() {
    if (!selectedSupervisor) return;
    setShowTransferOptions(false);
    setTransferConfirmed(true);
  }
  function showDeleteWarning(isDeleteOnly = false) {
    setShowWarningDialog(true);
    setIsDeleteOnly(isDeleteOnly);
  }
  
  

  function cancelWarning() {
    setShowWarningDialog(false);
  }

  async function proceedTransferAndDelete() {
    setShowWarningDialog(false);
    await handleTransferAndDelete();
    setShowSubmit(true);
  }

  function closeSubmitWidget() {
    setShowSubmit(false);
    setSelectedSupervisor(null);
    setTransferConfirmed(false);
    setNoSupervisors(false);
    setSelectedEmployee(null);
  }

  // Function to handle user confirmation in warning dialog
async function confirmDeletion() {
  setShowWarningDialog(false);
  await handleTransferAndDelete();  // your existing deletion logic
  setShowSubmit(true);               // show success screen after delete
}

  async function handleTransferAndDelete() {
    try {
      setIsDeleting(true);
      const isDriver = selectedRole.toLowerCase() === 'driver';
      
      if (noSupervisors || isOnlyDeleteDesignation) {
        // Use driver API for drivers, regular user API for others
        if (isDriver) {
          await driverApi.deleteDriver(selectedEmployee.employee_id);
          console.log("Deleted driver directly (no transfer needed)");
        } else {
          await deleteUser(selectedEmployee.employee_id);
          console.log("Deleted employee directly (no transfer needed)");
        }
      } else {
        // Verify before and after transfer by counting reportees
        const allBefore = await getAllUsers();
        const normalizeId = (v) => String(v ?? '').trim().toLowerCase();
        const targetFromIds = new Set([
          normalizeId(selectedEmployee.user_id),
          normalizeId(selectedEmployee.employee_id),
          normalizeId(selectedEmployee.display_id),
        ].filter(Boolean));
        const targetToIds = new Set([
          normalizeId(selectedSupervisor.user_id),
          normalizeId(selectedSupervisor.employee_id),
          normalizeId(selectedSupervisor.display_id),
        ].filter(Boolean));

        const extractRefs = (u) => {
          const vals = [];
          const pushVal = (val) => {
            if (val === undefined || val === null) return;
            if (Array.isArray(val)) { val.forEach(pushVal); return; }
            if (typeof val === 'object') { pushVal(val.user_id); pushVal(val.employee_id); pushVal(val.id); return; }
            const s = String(val).trim(); if (!s) return;
            if (s.includes(',')) { s.split(',').forEach(p => pushVal(p)); return; }
            vals.push(s.toLowerCase());
          };
          pushVal(u.reports_to); pushVal(u.reportsTo); pushVal(u.reporting_to); pushVal(u.reportingTo);
          pushVal(u.supervisor_id); pushVal(u.supervisorId); pushVal(u.manager_id); pushVal(u.managerId);
          pushVal(u.reports_to_user_id); pushVal(u.reports_to_employee_id);
          return Array.from(new Set(vals));
        };

        const beforeReportees = (allBefore || []).filter(u => {
          const refs = extractRefs(u);
          return refs.some(r => targetFromIds.has(r));
        });

        let transferSucceeded = false;
        try {
          // Use employee_id values like the Flutter app does
          await transferUsers(selectedEmployee.employee_id, selectedSupervisor.employee_id);
          console.log("Transfer attempted (backend determines reportees)");
          transferSucceeded = true;
        } catch (transferErr) {
          console.warn("Transfer failed or no users to transfer:", transferErr?.message || transferErr);
        }

        // Fetch after transfer to verify
        const allAfter = await getAllUsers();
        const afterStillUnderFrom = (allAfter || []).filter(u => {
          const refs = extractRefs(u);
          return refs.some(r => targetFromIds.has(r));
        });
        const afterNowUnderTo = (allAfter || []).filter(u => {
          const refs = extractRefs(u);
          return refs.some(r => targetToIds.has(r));
        });

        const movedCount = Math.max(0, beforeReportees.length - afterStillUnderFrom.length);
        console.log(`Reportees before: ${beforeReportees.length}, still under source: ${afterStillUnderFrom.length}, under target: ${afterNowUnderTo.length}, moved (approx): ${movedCount}`);

        // Show appropriate messages based on transfer results
        if (beforeReportees.length > 0) {
          if (afterStillUnderFrom.length > 0) {
            alert(`⚠️ WARNING: Only ${movedCount}/${beforeReportees.length} reportees were transferred. ${afterStillUnderFrom.length} employees may become orphaned (no supervisor).`);
          } else {
            alert(`✅ Successfully transferred ${movedCount}/${beforeReportees.length} reportees to ${selectedSupervisor.user_name}.`);
          }
        } else if (!transferSucceeded && beforeReportees.length > 0) {
          alert(`⚠️ WARNING: Transfer failed. ${beforeReportees.length} reportees under ${selectedEmployee.user_name} may become orphaned.`);
        }

        // Use driver API for drivers, regular user API for others
        if (isDriver) {
          await driverApi.deleteDriver(selectedEmployee.employee_id);
          console.log("Driver delete successful");
        } else {
          await deleteUser(selectedEmployee.employee_id);
          console.log("Delete successful");
        }
      }
      setShowSubmit(true);
    } catch (err) {
      console.error("Transfer/Delete error:", err);
      alert("Failed to transfer or delete employee: " + err.message);
    } finally {
      setSelectedEmployee(null);
      setSelectedSupervisor(null);
      setTransferConfirmed(false);
      setNoSupervisors(false);
      setIsDeleting(false);
      try {
        let usersRaw = [];
        let driversRaw = [];
        
        // Fetch all users
        try {
          usersRaw = await getAllUsers();
        } catch (err) {
          console.error('Failed to fetch all users:', err);
        }
        
        // Also fetch drivers if role is driver
        if (selectedRole.toLowerCase() === 'driver') {
          try {
            driversRaw = await driverApi.fetchAllDrivers();
          } catch (err) {
            console.error('Failed to fetch drivers:', err);
          }
        }
        
        // Combine and remove duplicates
        const allUsersRaw = [...usersRaw, ...driversRaw];
        const uniqueUsers = [];
        const seenIds = new Set();
        
        allUsersRaw.forEach(user => {
          const id = user.user_id || user.employee_id || user.id;
          if (id && !seenIds.has(String(id))) {
            seenIds.add(String(id));
            uniqueUsers.push(user);
          }
        });
        
        // Filter by selected role
        const normalizedRole = selectedRole.toLowerCase().trim();
        const filteredUsers = uniqueUsers.filter(user => {
          const userSpecRole = (user.spec_role || '').toLowerCase().trim();
          const userRoleName = (user.role_name || '').toLowerCase().trim();
          return userSpecRole === normalizedRole || userRoleName === normalizedRole;
        });
        
        // Store original user_id mapping before normalization (same as initial load)
        const userIdMap = new Map();
        filteredUsers.forEach(orig => {
          const key = orig.employee_id || orig.user_id || orig.id;
          if (key) {
            userIdMap.set(String(key), orig);
          }
        });
        
        const users = normalizeUsers(filteredUsers);
        // Fetch photos for each user (same as initial load)
        const withPhotos = await Promise.all(users.map(async (u) => {
          // Try to get user_id for photo fetching - use the original data from map
          const lookupKey = String(u.employee_id || u.user_id || u.id || '');
          const originalUser = userIdMap.get(lookupKey);
          
          // Priority: original user_id > normalized user_id > employee_id > id
          let photoUserId = null;
          if (originalUser) {
            // Use original user_id first, then fallback to employee_id or id
            photoUserId = originalUser.user_id || originalUser.employee_id || originalUser.id;
          } else {
            // Fallback to preserved original_user_id or normalized values
            photoUserId = u.original_user_id || 
                         (u.user_id && u.user_id !== 'N/A' ? u.user_id : null) ||
                         (u.employee_id || u.id || null);
          }
          
          if (!photoUserId || String(photoUserId).trim() === '' || photoUserId === 'N/A') {
            return { ...u, profile_image: null };
          }
          
          try {
            const photo = await getProfilePhotoWithS3Handling(String(photoUserId).trim());
            // Only set photo if we got a valid URL
            if (photo && 
                typeof photo === 'string' && 
                photo.trim() !== '' && 
                !photo.includes('undefined') && 
                photo !== 'null' && 
                photo !== 'undefined' &&
                (photo.startsWith('http://') || photo.startsWith('https://'))) {
              console.log(`✅ Successfully fetched photo for user ${photoUserId}:`, photo.substring(0, 50) + '...');
              return { ...u, profile_image: photo };
            } else {
              console.warn(`⚠️ Invalid photo URL for user ${photoUserId}:`, photo);
              return { ...u, profile_image: null };
            }
          } catch (error) {
            console.warn(`❌ Failed to fetch photo for user ${photoUserId}:`, error.message || error);
            return { ...u, profile_image: null };
          }
        }));
        setEmployees(withPhotos);
      } catch (e) {
        console.error("Failed to refresh employees after delete:", e);
      }
    }
  }

  // MODIFIED RETURN: MAIN LAYOUT
  return (
    <div className="w-full max-w-[971px] h-auto min-h-[400px] md:min-h-[723px] mx-auto relative flex flex-col items-center px-4 md:px-6 lg:px-0">
    {/* Keep dropdown aligned to the top */}
    <div className="mb-4 md:mb-6 self-start w-full">
      <div className="text-black text-lg md:text-xl font-bold font-['Figtree'] leading-normal mb-2">
        Select employee Designation
      </div>
      <CustomDropdown
        options={roles}
        selected={roles.find(r => r.value === selectedRole)?.display || ""}
        onSelect={setSelectedRole}
      />
    </div>

      {/* Main content below top frame */}
      <div className="w-full flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Left panel for search/filter/employees */}
        <div className="flex flex-col gap-4 md:gap-6 w-full md:w-96 min-h-full">
          <div className="text-black text-base md:text-lg font-bold font-['Figtree'] leading-snug">
            Filtering employees based on designation
          </div>
          <div className="mb-2">
            <SearchBar
              placeholder="Search employee by name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={(value) => setSearch(value)}
              className="w-full"
            />
          </div>
          <div className="w-full h-[400px] md:h-[488px] flex flex-col justify-start items-start gap-3 md:gap-4 overflow-y-auto pr-2 pb-2">
            {filteredEmployees.length === 0 && (
              <div className="p-4 text-gray-400 text-center w-full font-['Open_Sans'] text-base">No employees found.</div>
            )}
            {filteredEmployees.map(emp => (
              <div
                key={emp.user_id}
                onClick={() => {
                  setSelectedEmployee(emp);
                  setTransferConfirmed(false);
                  setSelectedSupervisor(null);
                  setShowTransferOptions(false);
                  setNoSupervisors(false);
                }}
                className={
                  'self-stretch p-2 md:p-3 rounded-xl inline-flex justify-start items-center gap-3 md:gap-6 cursor-pointer transition ' +
                  (selectedEmployee?.user_id === emp.user_id
                    ? "bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,rgba(10,128,137,0.09)_0%,rgba(255,255,255,0.72)_100%)] shadow-[2px_2px_12px_0px_rgba(34,139,34,0.12)] outline outline-1 outline-offset-[-1px] outline-white"
                    : "bg-neutral-50/10 outline outline-1 outline-offset-[-1px] outline-neutral-50/40")
                }
              >
                <img
                  className="w-12 h-12 md:w-14 md:h-14 rounded-xl object-cover bg-gray-200 flex-shrink-0"
                  src={emp.profile_image || 'https://placehold.co/56x56?text=No+Image'}
                  alt={emp.user_name || 'Employee'}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://placehold.co/56x56?text=No+Image';
                  }}
                />
                <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                  <div className="text-black text-base md:text-lg font-bold font-['Figtree'] leading-snug truncate w-full">{emp.user_name}</div>
                  <div className="text-black text-sm md:text-lg font-normal font-['Open_Sans'] leading-relaxed">ID : {emp.employee_id}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Right panel for details/transfer/delete - Hidden on mobile */}
        <div className="hidden md:flex flex-1 h-[580px] bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-neutral-300 backdrop-blur-[20px] flex-col p-6 lg:p-8 relative min-w-0 lg:min-w-[560px]">
          {selectedEmployee && (() => {
            const roleName = (selectedEmployee.role_name || selectedEmployee.spec_role || '').toLowerCase();
            const isSupervisor = roleName.includes('supervisor');
            return isSupervisor && (
              <div className="absolute top-3 right-6 flex flex-col items-end z-30">
                <InfoIcon onClick={() => setShowInfo(!showInfo)} />
                {showInfo && <InfoBox onClose={() => setShowInfo(false)} />}
              </div>
            );
          })()}
          {!selectedEmployee ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-base md:text-xl font-['Figtree'] px-4 text-center">
              Select an employee on the left
            </div>
          ) : (
            <div className="relative flex flex-col gap-6 h-full justify-start mt-2">
              {/* Profile + Role */}
              <div className="flex items-center gap-3 md:gap-4">
                <img
                  className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover bg-gray-200 flex-shrink-0"
                  src={selectedEmployee.profile_image || "https://placehold.co/80x80?text=No+Image"}
                  alt={selectedEmployee.user_name || 'Employee'}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://placehold.co/80x80?text=No+Image';
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-black text-xl md:text-2xl font-bold font-['Montserrat'] leading-7 truncate">{selectedEmployee.user_name}</div>
                  <div className="text-base md:text-lg text-gray-700 font-semibold font-['Open_Sans'] leading-relaxed">Role: <span className="font-normal">{selectedEmployee.role_name}</span></div>
                </div>
              </div>
              {/* Only show transfer text for supervisors */}
              {(() => {
                const roleName = (selectedEmployee.role_name || selectedEmployee.spec_role || '').toLowerCase();
                const isSupervisor = roleName.includes('supervisor');
                return isSupervisor && (
                  <div className="text-black text-sm md:text-base font-semibold">
                    Transfer the employees assigned under {selectedEmployee.user_name} to other employee
                  </div>
                );
              })()}
              {/* Transfer Button */}
              {!isOnlyDeleteDesignation && (
                <button
                  className="w-full bg-green-700 rounded-lg flex items-center justify-center gap-2 md:gap-3 py-2.5 md:py-3 text-white font-bold text-base md:text-xl border border-dashed border-green-700"
                  onClick={openTransferOptions}
                  disabled={showTransferOptions || isLoading || isDeleting}
                  style={{ opacity: showTransferOptions || isLoading || isDeleting ? 0.4 : 1 }}
                >
                  <img src={EmployeeGroupIcon} alt="group" className="h-5 w-5 md:h-6 md:w-6" />
                  <img src={ArrowUpIcon} alt="arrow" className="h-5 w-5 md:h-6 md:w-6" />
                  <img src={EmployeeIcon} alt="employee" className="h-5 w-5 md:h-6 md:w-6" />
                  <span className="ml-1 md:ml-2 text-sm md:text-base">Transfer employees to</span>
                </button>
              )}
              {/* Transfer info message */}
              {transferConfirmed && (selectedSupervisor || noSupervisors) && !isOnlyDeleteDesignation && (
                <div className="w-full px-3 md:px-4 py-2 rounded-xl border border-blue-300 bg-blue-50 text-sm md:text-lg">
                  employees assigned for <span className="text-green-700">{selectedEmployee.user_name}</span>
                  {noSupervisors ? (
                    <b> will be deleted directly (no supervisors available)</b>
                  ) : (
                    <>
                      <b> will be transferred to </b>
                      <span className="text-blue-700">{selectedSupervisor.user_name}</span>
                    </>
                  )}
                </div>
              )}
              {/* Delete Button */}
              <button
  className={`w-full h-12 md:h-14 mt-4 rounded-lg flex justify-center items-center text-white text-base md:text-xl font-bold font-['Figtree'] ${
    isOnlyDeleteDesignation || (transferConfirmed && (selectedSupervisor || noSupervisors))
      ? 'bg-gradient-to-br from-red-400 to-red-800'
      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
  }`}
  onClick={() => showDeleteWarning(isOnlyDeleteDesignation)}
  // Changed to only show dialog here
  disabled={
    isOnlyDeleteDesignation
      ? false
      : !(transferConfirmed && (selectedSupervisor || noSupervisors))
  }
>
  <span className="text-sm md:text-base">{isOnlyDeleteDesignation ? 'Delete Employee' : 'Transfer and Delete the Employee'}</span>
</button>
            </div>
          )}
        </div>
      </div>
      {/* Supervisor Transfer Modal */}
      {showTransferOptions && (
        <>
          {/* Mobile: Bottom overlay */}
          <div 
            className="md:hidden fixed inset-0 z-[110] bg-black/50 flex items-end"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowTransferOptions(false);
              }
            }}
          >
            <div 
              className="w-full h-[85vh] bg-white rounded-t-3xl shadow-lg flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="text-black text-2xl font-bold font-['Montserrat']">Transfer employees to</div>
                <button 
                  onClick={() => setShowTransferOptions(false)} 
                  className="w-10 h-10 flex items-center justify-center bg-stone-950/10 rounded-full"
                  aria-label="Close"
                >
                  <img src={CloseIcon} alt="close" className="w-6 h-6" />
                </button>
              </div>
              
              {/* Search Bar */}
              <div className="px-4 pt-4 pb-2">
                <SearchBar
                  placeholder="Search supervisor by name or ID..."
                  value={supervisorSearch}
                  onChange={(e) => setSupervisorSearch(e.target.value)}
                  onSearch={(value) => setSupervisorSearch(value)}
                  className="w-full"
                />
              </div>
              
              {/* Supervisor List */}
              <div className="flex-1 overflow-y-auto px-4 py-2">
                {filteredSupervisors.length === 0 && (
                  <div className="p-4 text-gray-400 text-center font-['Open_Sans']">No supervisors found.</div>
                )}
                {filteredSupervisors.map((sup) => (
                  <div
                    key={sup.user_id}
                    className={`w-full pb-3 mb-2 border-b border-neutral-300 flex justify-between items-center cursor-pointer rounded-lg px-2 py-2 transition ${
                      selectedSupervisor?.user_id === sup.user_id ? 'bg-green-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedSupervisor(sup)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <img 
                        className="w-12 h-12 rounded-lg object-cover bg-gray-200" 
                        src={sup.profile_image || "https://placehold.co/48x48?text=No+Image"} 
                        alt={sup.user_name || 'Supervisor'}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = 'https://placehold.co/48x48?text=No+Image';
                        }}
                      />
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="text-black text-lg font-bold font-['Figtree'] truncate">{sup.user_name}</div>
                        <div className="text-black text-base font-normal font-['Open_Sans'] truncate">{sup.role_name}</div>
                      </div>
                    </div>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-2">
                      {selectedSupervisor?.user_id === sup.user_id ? (
                        <div className="w-6 h-6 bg-green-700 border-2 border-green-700 flex items-center justify-center rounded">
                          <svg className="w-4 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-6 h-6 bg-zinc-100 border border-black/30 rounded"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Confirm Button */}
              {selectedSupervisor && (
                <div className="w-full flex justify-center p-4 border-t border-gray-200">
                  <button 
                    className="w-full max-w-xs h-14 px-6 py-3 bg-green-700 rounded-lg flex justify-center items-center text-white text-xl font-bold font-['Figtree']" 
                    onClick={confirmTransfer}
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Desktop: Centered modal */}
          <div className="hidden md:flex fixed inset-0 z-50 bg-black/20 justify-center items-center p-4" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTransferOptions(false);
            }
          }}>
            <div className="w-full max-w-[464px] h-[90vh] max-h-[682px] relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_rgba(0,0,0,0.10)] outline outline-1 outline-neutral-300/20 backdrop-blur-[30px] flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="w-full px-4 md:px-6 pt-4 md:pt-6 mb-4 md:mb-6 flex items-center justify-between">
                <div className="text-black text-xl md:text-2xl font-bold font-['Montserrat']">Transfer employees to</div>
                <button onClick={() => setShowTransferOptions(false)} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-stone-950/10 rounded hover:bg-red-200 flex-shrink-0">
                  <img src={CloseIcon} alt="close" className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>
              <div className="w-full px-4 md:px-6 mb-6 md:mb-8">
                <SearchBar
                  placeholder="Search supervisor by name or ID..."
                  value={supervisorSearch}
                  onChange={(e) => setSupervisorSearch(e.target.value)}
                  onSearch={(value) => setSupervisorSearch(value)}
                  className="w-full"
                />
              </div>
              <div className="w-full flex-1 flex flex-col gap-2 overflow-y-auto px-4 md:px-6">
                {filteredSupervisors.length === 0 && (
                  <div className="p-4 text-gray-400 text-center font-['Open_Sans'] text-sm md:text-base">No supervisors found.</div>
                )}
                {filteredSupervisors.map((sup) => (
                  <div
                    key={sup.user_id}
                    className={`w-full pb-3 border-b border-neutral-300 flex justify-between items-center cursor-pointer ${selectedSupervisor?.user_id === sup.user_id ? 'bg-white' : ''}`}
                    onClick={() => setSelectedSupervisor(sup)}
                  >
                    <div className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-1.5 justify-start rounded-xl transition flex-1 min-w-0">
                      <img 
                        className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover bg-gray-200 flex-shrink-0" 
                        src={sup.profile_image || "https://placehold.co/48x48?text=No+Image"} 
                        alt={sup.user_name || 'Supervisor'}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = 'https://placehold.co/48x48?text=No+Image';
                        }}
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="text-black text-base md:text-lg font-bold font-['Figtree'] truncate">{sup.user_name}</div>
                        <div className="text-black text-sm md:text-lg font-normal font-['Open_Sans'] truncate">{sup.role_name}</div>
                      </div>
                    </div>
                    <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0 ml-2">
                      {selectedSupervisor?.user_id === sup.user_id ? (
                        <div className="w-5 h-5 md:w-6 md:h-6 bg-green-700 border-2 border-green-700 flex items-center justify-center rounded">
                          <div className="w-3 h-2 md:w-4 md:h-3 bg-white rounded"></div>
                        </div>
                      ) : (
                        <div className="w-5 h-5 md:w-6 md:h-6 bg-zinc-100 border border-black/30 rounded"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {selectedSupervisor && (
                <div className="w-full flex justify-center mb-4 md:mb-6 mt-2 px-4 md:px-6">
                  <button className="w-full max-w-[192px] md:w-48 h-12 md:h-14 px-6 py-3 bg-green-700 rounded-lg flex justify-center items-center text-white text-base md:text-xl font-bold font-['Figtree']" onClick={confirmTransfer}>Confirm</button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {showWarningDialog && (
  <div className="fixed inset-0 flex justify-center items-center z-60 bg-black/30 p-4">
    <div className="w-full max-w-[464px] p-6 md:p-8 bg-neutral-50/50 rounded-xl shadow-lg outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] flex flex-col gap-4 md:gap-6">
      <div className="flex justify-between items-center">
        <div className="text-black text-xl md:text-2xl font-bold font-figtree leading-7">Warning</div>
        <button onClick={() => setShowWarningDialog(false)} aria-label="Close warning" className="w-10 h-10 md:w-12 md:h-12 bg-stone-950/10 rounded flex items-center justify-center flex-shrink-0">
          <img src={CloseIcon} alt="close" className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </div>
      <div className="text-black text-base md:text-lg font-normal font-openSans leading-relaxed">
        {isDeleteOnly ? (
          <>
            Are you sure, do you want to delete{' '}
            <span className="font-bold">{selectedEmployee?.user_name || 'this employee'}</span>?
          </>
        ) : (
          <>
            Are you sure, do you want to transfer all the assigned employees to{' '}
            <span className="font-bold">{selectedSupervisor?.user_name || 'selected Supervisor'}</span>
            {' '}and delete{' '}
            <span className="font-bold">{selectedEmployee?.user_name || 'selected Employee'}</span>?
          </>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-start items-stretch sm:items-start gap-3 md:gap-6">
        <button
          onClick={() => setShowWarningDialog(false)}
          className="w-full sm:w-48 h-12 md:h-14 px-6 py-3 bg-green-700 rounded-lg text-white text-base md:text-xl font-bold font-figtree"
        >
          Cancel
        </button>
        <button
          onClick={confirmDeletion}  // Confirm button runs deletion logic then success
          className="w-full sm:w-48 h-12 md:h-14 px-6 py-3 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-red-500 text-red-500 text-base md:text-xl font-bold font-figtree"
        >
          Yes
        </button>
      </div>
    </div>
  </div>
)}
      {/* Loading overlay when deleting */}
      {isDeleting && (
        <div className="fixed inset-0 flex justify-center items-center z-[80] bg-black/50 p-4">
          <div className="w-full max-w-[464px] p-6 md:p-8 bg-neutral-50/50 rounded-xl shadow-lg outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] flex flex-col gap-4 md:gap-6 items-center">
            <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-green-700 border-t-transparent rounded-full animate-spin"></div>
            <div className="text-black text-lg md:text-xl font-bold font-figtree leading-7 text-center">
              Deleting employee...
            </div>
            <div className="text-black text-sm md:text-base font-normal font-openSans leading-relaxed text-center">
              Please wait while we delete the employee and transfer their assigned employees.
            </div>
          </div>
        </div>
      )}
      {showSubmit && <SubmitWidget onClose={closeSubmitWidget} />}
      
      {/* Mobile Overlay for Employee Details */}
      {selectedEmployee && (
        <div className="md:hidden fixed inset-0 z-[100] bg-black/50 flex items-end">
          <div className="w-full h-[85vh] bg-white rounded-t-3xl shadow-lg flex flex-col overflow-hidden">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="text-black text-xl font-bold font-['Figtree']">Employee Details</div>
              <button
                onClick={() => {
                  setSelectedEmployee(null);
                  setTransferConfirmed(false);
                  setSelectedSupervisor(null);
                  setShowTransferOptions(false);
                  setNoSupervisors(false);
                }}
                className="w-10 h-10 flex items-center justify-center bg-stone-950/10 rounded-full"
                aria-label="Close"
              >
                <img src={CloseIcon} alt="close" className="w-6 h-6" />
              </button>
            </div>
            
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6">
              {(() => {
                const roleName = (selectedEmployee.role_name || selectedEmployee.spec_role || '').toLowerCase();
                const isSupervisor = roleName.includes('supervisor');
                return (
                  <div className="flex flex-col gap-6">
                    {/* Profile + Role */}
                    <div className="flex items-center gap-3 md:gap-4">
                      <img
                        className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover bg-gray-200 flex-shrink-0"
                        src={selectedEmployee.profile_image || "https://placehold.co/80x80?text=No+Image"}
                        alt={selectedEmployee.user_name || 'Employee'}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = 'https://placehold.co/80x80?text=No+Image';
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-black text-xl md:text-2xl font-bold font-['Montserrat'] leading-7 truncate">{selectedEmployee.user_name}</div>
                        <div className="text-base md:text-lg text-gray-700 font-semibold font-['Open_Sans'] leading-relaxed">Role: <span className="font-normal">{selectedEmployee.role_name}</span></div>
                      </div>
                    </div>
                    
                    {/* Info icon for supervisors */}
                    {isSupervisor && (
                      <div className="flex items-center justify-end relative">
                        <InfoIcon onClick={() => setShowInfo(!showInfo)} />
                        {showInfo && (
                          <div className="absolute bottom-12 right-0 w-64 md:w-72 p-3 md:p-4 bg-neutral-50/50 rounded-xl shadow-lg outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px] z-50">
                            <div className="text-black text-sm md:text-base font-normal font-['Open_Sans'] leading-tight">
                              To Delete an employee, Please transfer the assigned employees to other employee
                            </div>
                            <button
                              onClick={() => setShowInfo(false)}
                              className="absolute top-2 right-2 w-6 h-6 bg-stone-950/10 rounded-full flex items-center justify-center"
                              aria-label="Close info"
                            >
                              <img src={CloseIcon} alt="close-info" className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Transfer text for supervisors */}
                    {isSupervisor && (
                      <div className="text-black text-sm md:text-base font-semibold">
                        Transfer the employees assigned under {selectedEmployee.user_name} to other employee
                      </div>
                    )}
                    
                    {/* Transfer Button */}
                    {!isOnlyDeleteDesignation && (
                      <button
                        className="w-full bg-green-700 rounded-lg flex items-center justify-center gap-2 md:gap-3 py-2.5 md:py-3 text-white font-bold text-base md:text-xl border border-dashed border-green-700"
                        onClick={openTransferOptions}
                        disabled={showTransferOptions || isLoading || isDeleting}
                        style={{ opacity: showTransferOptions || isLoading || isDeleting ? 0.4 : 1 }}
                      >
                        <img src={EmployeeGroupIcon} alt="group" className="h-5 w-5 md:h-6 md:w-6" />
                        <img src={ArrowUpIcon} alt="arrow" className="h-5 w-5 md:h-6 md:w-6" />
                        <img src={EmployeeIcon} alt="employee" className="h-5 w-5 md:h-6 md:w-6" />
                        <span className="ml-1 md:ml-2 text-sm md:text-base">Transfer employees to</span>
                      </button>
                    )}
                    
                    {/* Transfer info message */}
                    {transferConfirmed && (selectedSupervisor || noSupervisors) && !isOnlyDeleteDesignation && (
                      <div className="w-full px-3 md:px-4 py-2 rounded-xl border border-blue-300 bg-blue-50 text-sm md:text-lg">
                        employees assigned for <span className="text-green-700">{selectedEmployee.user_name}</span>
                        {noSupervisors ? (
                          <b> will be deleted directly (no supervisors available)</b>
                        ) : (
                          <>
                            <b> will be transferred to </b>
                            <span className="text-blue-700">{selectedSupervisor.user_name}</span>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Delete Button */}
                    <button
                      className={`w-full h-12 md:h-14 mt-4 rounded-lg flex justify-center items-center text-white text-base md:text-xl font-bold font-['Figtree'] ${
                        isOnlyDeleteDesignation || (transferConfirmed && (selectedSupervisor || noSupervisors))
                          ? 'bg-gradient-to-br from-red-400 to-red-800'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                      onClick={() => showDeleteWarning(isOnlyDeleteDesignation)}
                      disabled={
                        isOnlyDeleteDesignation
                          ? false
                          : !(transferConfirmed && (selectedSupervisor || noSupervisors))
                      }
                    >
                      <span className="text-sm md:text-base">{isOnlyDeleteDesignation ? 'Delete Employee' : 'Transfer and Delete the Employee'}</span>
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
